import React from "react";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { notification } from "antd";
import { extend } from "umi-request";
import { MediaPurpose } from "@/typedefs/media";
import { checkForNewBuild } from "@/utils/buildCheck";
import { withClientTypeHeader } from "@/utils/clientType";
import { shouldBlockWriteWhileImpersonating } from "@/utils/impersonation";
import { pathnameEndsWithGuid, redirectToLogin } from "@/utils/route";
import { revokeToken, refreshToken } from "@/utils/session";
import { getLocalStorage } from "@/utils/storage";
import { getDeviceHeaders } from "@/services/deviceDetails";
import ErrorRequestMessage from "@/components/ErrorRequestMessage/ErrorRequestMessage";
import { showUpdateAvailableBanner } from "@/components/UpdateAvailableBanner";
const DEFAULT_ERROR_TITLE = "Error";
const DEFAULT_ERROR_DESCRIPTION = "Sorry, an unknown error occurred. Please try again later or contact Autosled support for further assistance.";
const NETWORK_ERROR_TITLE = "Network Error";
const NETWORK_ERROR_DESCRIPTION = "No response from the server. Please check your internet connection.";
const ABP_DEFAULT_ERRORS_TEXT = {
    LOGIN: "You are not allowed to login! Your account is inactive or needs to confirm your email/phone number.",
};
const CUSTOM_ERROR_MAPPINGS = {
    [ABP_DEFAULT_ERRORS_TEXT.LOGIN]: "Login not allowed. Your account is inactive, or your email address has not been verified. If you just signed up, please check your email and follow the verification link to activate your account.",
};
const doNotShowANotificationEndpoints = {
    "/connect/token": {},
    "/api/identity-service/verification/create-session": {},
    "/api/identity-service/verification/link-session": {},
    "/api/identity-service/verification/session/:id/status": {},
    "/api/identity-service/verification/session/:id/cancel": {},
    "/api/core-service/shipments/admin/:id/hold": {},
    "/api/core-service/shipments/:id/hold-step": {},
    "/api/core-service/transporters/info-from-dot": {},
    "/api/core-service/payments/by-load": {},
    "/api/media-service/media/upload": {
        paramsSets: [
            { mediaPurpose: MediaPurpose.BillOfLading.toString() },
            { mediaPurpose: MediaPurpose.BillOfLadingAdditional.toString() },
        ],
    },
    "/api/account/verify-authenticator-code": {},
    "/api/account/confirm-phone-number": {},
    "/api/tag-service/tags": {
        methods: ["POST", "PUT"], // We handle these erros in the form, but we want to show notifications for GET and DELETE requests
    },
    "/api/core-service/loads/accept": {
        methods: ["POST"],
    },
    "/api/core-service/loads/accept-many": {
        methods: ["POST"], // Errors are shown in the dispatch modal
    },
};
function splitURLandParams(urlString) {
    if (!urlString) {
        return ["", new URLSearchParams()];
    }
    const url = new URL(urlString);
    return [url.pathname, url.searchParams];
}
function getCustomErrorMessage(originalMessage, url) {
    // Only current case applies to Login errors
    if (url && url.includes("connect/token")) {
        // Workaround for login error code related to https://github.com/abpframework/abp/issues/17411
        return CUSTOM_ERROR_MAPPINGS[originalMessage] || originalMessage;
    }
    return originalMessage;
}
// exported for unit tests
export const errorHandler = async (error) => {
    const { response } = error;
    let errorText = null;
    if (response && response.status) {
        errorText = ("message" in response && response.message);
        const { url } = response;
        if (url.includes("connect/token")) {
            notification.error({
                message: `Error`,
                description: "invalid login credentials",
            });
        }
        else {
            // PD-3751: a stale client sending an outdated payload gets a validation error; show "refresh" instead of it
            const isStaleBuild = (response.status === 400 || response.status === 422) && (await checkForNewBuild());
            if (isStaleBuild)
                showUpdateAvailableBanner();
            await response
                .json()
                .then(r => {
                let displayExistingError = true;
                if (r.status === 404)
                    return;
                if (r.error && r.error.message)
                    errorText = r.error.message;
                else if (r?.errors) {
                    Object.values(r?.errors).forEach(errorMessage => {
                        if (!isStaleBuild) {
                            notification.error({
                                message: "Error",
                                description: errorMessage,
                            });
                        }
                        displayExistingError = false;
                    });
                }
                if (displayExistingError && !isStaleBuild) {
                    notification.error({
                        message: `Error`,
                        description: errorText,
                    });
                }
            })
                .catch(e => console.error(e));
        }
    }
    else if (!response) {
        const { request } = error;
        const { url: rawUrl, options } = request;
        const { method } = options;
        const [rawPathname, params] = splitURLandParams(rawUrl);
        let pathname = rawPathname;
        const isNetworkConnected = navigator.onLine;
        let notificationType = "error";
        let errorToastTitle = DEFAULT_ERROR_TITLE;
        let errorToastDescription = DEFAULT_ERROR_DESCRIPTION;
        if (!isNetworkConnected) {
            errorToastTitle = NETWORK_ERROR_TITLE;
            errorToastDescription = NETWORK_ERROR_DESCRIPTION;
        }
        else if (error.message && typeof error.message === "string") {
            notificationType = "error";
            try {
                const parsedMessage = JSON.parse(error.message);
                errorToastDescription = parsedMessage.message;
                notificationType = parsedMessage.code === 403 ? "info" : "error";
                errorToastTitle = "Information";
            }
            catch (e) {
                if (typeof error.message === "string") {
                    errorToastDescription = `Sorry, an error occurred: \n\n${error.message}`;
                }
                else {
                    errorToastDescription = `Sorry, an error occurred.`;
                }
            }
        }
        const endsWithGuid = pathnameEndsWithGuid(pathname);
        if (endsWithGuid) {
            pathname = pathname.split("/").slice(0, -1).join("/");
        }
        pathname = pathname.replace(/^\/api\/identity-service\/verification\/session\/[^/]+\/(status|cancel)$/, "/api/identity-service/verification/session/:id/$1");
        pathname = pathname
            .replace(/^\/api\/core-service\/shipments\/admin\/[^/]+\/hold$/, "/api/core-service/shipments/admin/:id/hold")
            .replace(/^\/api\/core-service\/shipments\/[^/]+\/hold-step$/, "/api/core-service/shipments/:id/hold-step");
        const endpointNotConsidered = !doNotShowANotificationEndpoints[pathname];
        const { methods, paramsSets: matchingParams } = doNotShowANotificationEndpoints[pathname] ?? {};
        const methodMatches = !method || (methods && method && !methods.includes(method));
        let paramsMatches = matchingParams === undefined;
        if (Array.isArray(matchingParams) && matchingParams.length > 0) {
            // Endpoint tagged as notifiable
            // Check if all params are in the list
            paramsMatches = matchingParams.every(combination => {
                const entries = Object.entries(combination);
                // If the specific params combination is found, do not notify
                return entries.every(([key, value]) => params.get(key) !== value);
            });
        }
        const shouldNotify = endpointNotConsidered || (methodMatches && paramsMatches);
        // PD-3751: API errors thrown by the response interceptor carry `status` but no `response`
        const errorStatus = error.status;
        const isStaleBuild = (errorStatus === 400 || errorStatus === 422) && (await checkForNewBuild());
        if (isStaleBuild)
            showUpdateAvailableBanner();
        if (shouldNotify && !isStaleBuild) {
            notification[notificationType]({
                message: errorToastTitle,
                description: React.createElement(ErrorRequestMessage, { errorMessage: errorToastDescription }),
            });
        }
        return {
            error: error.message ?? "Error",
            code: error.code,
            status: error.status,
        };
    }
    if (!Array.isArray(response)) {
        if (response.status === 404) {
            return [];
        }
    }
    else {
        return response;
    }
    if (errorText && errorText.startsWith("Cannot post multiple instances of vin")) {
        return errorText;
    }
    return null;
};
const isOwnApiUrl = (url) => url.startsWith(REACT_APP_API_BASE_URL_NEW) || url.startsWith(REACT_APP_IDENTITY_SERVER_BASE_URL);
const request = extend({
    errorHandler,
    getResponse: true,
});
request.interceptors.request.use((url, options) => {
    if (shouldBlockWriteWhileImpersonating(options.method, url)) {
        notification.error({
            message: "Action Not Allowed",
            description: "Cannot perform this action while impersonating.",
        });
        throw new Error("You are not allowed to access this resource.");
    }
    const dataAuth = getLocalStorage("dataAuth");
    const token = dataAuth?.access_token;
    const deviceHeaders = isOwnApiUrl(url) ? getDeviceHeaders() : {};
    if (token) {
        return {
            url,
            options: {
                ...options,
                headers: withClientTypeHeader(url, {
                    ...options.headers,
                    ...deviceHeaders,
                    Authorization: `Bearer ${token}`,
                }),
                ...(options.signal && { signal: options.signal }),
            },
        };
    }
    return {
        url,
        options: {
            ...options,
            headers: withClientTypeHeader(url, { ...options.headers, ...deviceHeaders }),
            ...(options.signal && { signal: options.signal }),
        },
    };
});
// Token refresh queue - ensures only one refresh happens at a time
let isRefreshing = false;
let pendingRequests = [];
const onTokenRefreshed = (token) => {
    pendingRequests.forEach(callback => callback(token));
    pendingRequests = [];
};
const addPendingRequest = (callback) => {
    pendingRequests.push(callback);
};
const loggableURLs = [REACT_APP_API_BASE_URL_NEW, REACT_APP_IDENTITY_SERVER_BASE_URL];
// @ts-expect-error We need to be able to return null to to make the 2FA feature work
request.interceptors.response.use(async (response, options) => {
    const clone = response.clone();
    let result;
    try {
        result = await clone.json();
    }
    catch (error) {
        //
    }
    let microservicesBaseURL;
    for (let i = 0; i < loggableURLs.length; i++) {
        if (response.url.includes(loggableURLs[i])) {
            microservicesBaseURL = loggableURLs[i];
        }
    }
    if (microservicesBaseURL) {
        const endpoint = response.url.slice(microservicesBaseURL.length);
        const isErrorHTTPStatus = response.status >= 400;
        const logMessage = `Network Log - STATUS: ${response.status}, METHOD: ${options.method}, ENDPOINT: ${endpoint}`;
        const logData = {
            response: {
                headers: JSON.stringify(response.headers),
                status: response.status,
                url: response.url,
                body: result,
            },
        };
        if (isErrorHTTPStatus) {
            datadogLogs.logger.error(logMessage, logData);
            if (response.status >= 500) {
                datadogRum.addError(new Error(`Internal Server Error - ${response.status} - ${endpoint}`));
            }
        }
        else {
            datadogLogs.logger.log(logMessage, logData);
        }
    }
    const { error, error_description: errorDescription } = result || {};
    // Success - return response
    if (response && response?.ok) {
        return response;
    }
    const isTokenEndpoint = response.url === `${REACT_APP_IDENTITY_SERVER_BASE_URL}/connect/token`;
    // Handle 401 Unauthorized
    if (response.status === 401) {
        // Don't retry token endpoint requests
        if (isTokenEndpoint) {
            const dataAuth = getLocalStorage("dataAuth");
            const token = dataAuth?.refresh_token;
            if (token) {
                await revokeToken({ token });
                notification.warning({
                    message: "Session Expired",
                    description: "Your session has expired. Please log in again to continue.",
                    duration: 5,
                });
            }
            redirectToLogin();
            return null;
        }
        // For regular API requests, refresh token and retry
        if (!isRefreshing) {
            isRefreshing = true;
            refreshToken()
                .then(refreshResponse => {
                if (refreshResponse && "ok" in refreshResponse && refreshResponse.ok) {
                    const dataAuth = getLocalStorage("dataAuth");
                    const newToken = dataAuth?.access_token;
                    isRefreshing = false;
                    onTokenRefreshed(newToken);
                    return newToken;
                }
                throw new Error("Token refresh failed");
            })
                .catch(async () => {
                isRefreshing = false;
                pendingRequests = [];
                const dataAuth = getLocalStorage("dataAuth");
                const token = dataAuth?.refresh_token;
                if (token) {
                    await revokeToken({ token });
                    notification.warning({
                        message: "Session Expired",
                        description: "Your session has expired. Please log in again to continue.",
                        duration: 5,
                    });
                }
                redirectToLogin();
                return null;
            });
        }
        // Wait for token refresh and retry request
        return new Promise(resolve => {
            addPendingRequest(newToken => {
                if (!newToken) {
                    resolve(null);
                    return;
                }
                // Retry original request with new token
                fetch(response.url, {
                    ...options,
                    headers: {
                        ...options.headers,
                        Authorization: `Bearer ${newToken}`,
                    },
                })
                    .then(resolve)
                    .catch(() => resolve(null));
            });
        });
    }
    if (response.status === 403) {
        throw Object.assign(new Error(JSON.stringify({
            message: result?.error?.message,
            code: response.status,
            data: result?.error?.data,
        })), { code: result?.error?.code, status: response.status });
    }
    // Handle 400 on token endpoint (bad credentials or impersonation)
    if (response.status === 400 && isTokenEndpoint) {
        const params = new URLSearchParams(options.body);
        const tryingToImpersonate = params.get("UserId") !== null;
        if (tryingToImpersonate) {
            notification.error({
                message: "Error",
                description: "Something went wrong when trying to impersonate this user",
            });
            return null;
        }
    }
    // handle two-factor authentication error
    if (isTokenEndpoint && errorDescription === "RequiresTwoFactor") {
        return {
            // @ts-expect-error I didn't know how to solve this type issue
            error: errorDescription,
            data: result,
        };
    }
    // Custom error message mapping for specific backend errors
    const customErrorMessage = getCustomErrorMessage(errorDescription || error?.details || error?.message, response.url);
    const apiError = Object.assign(new Error(customErrorMessage), { code: error?.code, status: response.status });
    throw apiError;
});
export default request;
